/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CampaignCard from "./CampaignCard";
import t from "../../../theme";

const AUTOPLAY_MS = 4000;
const TRANSITION_MS = 650;

const BREAKPOINTS = [
  { maxWidth: 480, slides: 1 },
  { maxWidth: 640, slides: 2 },
  { maxWidth: 1180, slides: 3 },
];
const DEFAULT_SLIDES = 4;

const getSlidesToShow = () => {
  if (typeof window === "undefined") return DEFAULT_SLIDES;
  const width = window.innerWidth;
  const match = BREAKPOINTS.find((bp) => width <= bp.maxWidth);
  return match ? match.slides : DEFAULT_SLIDES;
};

const getCampaignKey = (campaign, index) =>
  campaign?.uuid || campaign?.campaign_uuid || campaign?.id || index;

const styles = {
  viewport: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 20,
  },
  track: {
    display: "flex",
  },
  // With fewer campaigns than slots there is nothing to scroll, so the slides
  // would otherwise sit against the left edge with dead space to the right.
  // Only safe while !canLoop — the looping track is positioned by translateX,
  // which assumes slides start at the left edge.
  trackCentered: {
    justifyContent: "center",
  },
  slide: {
    flexShrink: 0,
    boxSizing: "border-box",
    padding: "0 6px",
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 3,
    width: 34,
    height: 34,
    background: "rgba(15, 23, 42, 0.55)",
    color: "#ffffff",
    backdropFilter: "blur(4px)",
    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.35)",
    transition: t.transition.fast,
    "&:hover": {
      background: "rgba(15, 23, 42, 0.78)",
    },
  },
  navButtonPrev: {
    left: 8,
  },
  navButtonNext: {
    right: 8,
  },
  dots: {
    display: "flex",
    justifyContent: "center",
    gap: 6,
    marginTop: 12,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    border: 0,
    padding: 0,
    background: "rgba(84, 172, 52, 0.25)",
    cursor: "pointer",
    transition: t.transition.fast,
  },
  dotActive: {
    background: "#54ac34",
    width: 20,
  },
  skeletonRow: {
    display: "flex",
    gap: 12,
  },
  skeletonSlide: {
    flex: "1 1 0",
    minWidth: 0,
    borderRadius: 20,
  },
};

const CampaignsCarousel = ({ campaigns, isLoading, onOpenCampaign }) => {
  const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow);
  const count = campaigns.length;
  const canLoop = count > slidesToShow;
  const [index, setIndex] = useState(() => (canLoop ? slidesToShow : 0));
  const [instant, setInstant] = useState(false);
  const pausedRef = useRef(false);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  useEffect(() => {
    const onResize = () => setSlidesToShow(getSlidesToShow());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setIndex(canLoop ? slidesToShow : 0);
  }, [count, slidesToShow, canLoop]);

  useEffect(() => {
    if (!canLoop) return undefined;
    const id = setInterval(() => {
      if (!pausedRef.current) setIndex((i) => i + 1);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
    // Restarting on every index change gives manual nav a fresh autoplay window.
  }, [canLoop, index]);

  // Seamless loop in both directions: cloned slides pad each end of the
  // track, and once one slides fully into view we snap back to the
  // equivalent real position with the transition disabled for that frame.
  useEffect(() => {
    if (!canLoop) return undefined;
    const overflow = index >= slidesToShow + count;
    const underflow = index < slidesToShow;
    if (!overflow && !underflow) return undefined;
    const id = setTimeout(() => {
      setInstant(true);
      setIndex((i) => (overflow ? i - count : i + count));
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setInstant(false));
      });
    }, TRANSITION_MS);
    return () => clearTimeout(id);
  }, [index, count, slidesToShow, canLoop]);

  const paddedItems = useMemo(() => {
    if (!canLoop) return campaigns;
    return [
      ...campaigns.slice(-slidesToShow),
      ...campaigns,
      ...campaigns.slice(0, slidesToShow),
    ];
  }, [campaigns, canLoop, slidesToShow]);

  if (isLoading) {
    return (
      <div css={styles.skeletonRow}>
        {Array.from({ length: slidesToShow }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            css={styles.skeletonSlide}
            sx={{ aspectRatio: "3 / 2", bgcolor: "rgba(84, 172, 52, 0.12)" }}
          />
        ))}
      </div>
    );
  }

  const activeDot = ((index - slidesToShow) % count + count) % count;

  // Tapping the small arrow buttons isn't the natural gesture on a
  // touchscreen — swipe is, so the viewport tracks a horizontal drag and
  // advances once it crosses a threshold, same as tapping an arrow would.
  const SWIPE_THRESHOLD_PX = 40;

  const onTouchStart = (event) => {
    if (!canLoop) return;
    pausedRef.current = true;
    touchStartX.current = event.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (event) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = event.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (touchStartX.current === null) return;
    if (touchDeltaX.current > SWIPE_THRESHOLD_PX) setIndex((i) => i - 1);
    else if (touchDeltaX.current < -SWIPE_THRESHOLD_PX) setIndex((i) => i + 1);
    touchStartX.current = null;
    touchDeltaX.current = 0;
    pausedRef.current = false;
  };

  return (
    <div>
      <div
        css={styles.viewport}
        onMouseEnter={() => {
          pausedRef.current = true;
        }}
        onMouseLeave={() => {
          pausedRef.current = false;
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          css={[styles.track, !canLoop && styles.trackCentered]}
          style={{
            transform: `translateX(-${index * (100 / slidesToShow)}%)`,
            transition: instant
              ? "none"
              : `transform ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        >
          {paddedItems.map((campaign, i) => (
            <div
              key={`${getCampaignKey(campaign, i)}-${i}`}
              css={styles.slide}
              style={{ width: `${100 / slidesToShow}%` }}
            >
              <CampaignCard campaign={campaign} onOpen={onOpenCampaign} compact />
            </div>
          ))}
        </div>

        {canLoop && (
          <>
            <IconButton
              aria-label="Previous campaigns"
              css={[styles.navButton, styles.navButtonPrev]}
              onClick={() => setIndex((i) => i - 1)}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              aria-label="Next campaigns"
              css={[styles.navButton, styles.navButtonNext]}
              onClick={() => setIndex((i) => i + 1)}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </>
        )}
      </div>

      {canLoop && (
        <div css={styles.dots}>
          {campaigns.map((campaign, i) => (
            <button
              key={getCampaignKey(campaign, i)}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              css={[styles.dot, activeDot === i && styles.dotActive]}
              onClick={() => {
                setInstant(false);
                setIndex(slidesToShow + i);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

CampaignsCarousel.propTypes = {
  campaigns: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onOpenCampaign: PropTypes.func.isRequired,
};

export default CampaignsCarousel;
